"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import {
  fetchEstablishments,
  toggleEstablishmentStatus,
  updateEstablishment,
  activateEstablishment,
  deactivateEstablishment,
  createEstablishment,
} from "@/store/modules/establishments/thunks";
import { clearEstablishments } from "@/store/modules/establishments/slice";
import { Establishment } from "@/store/modules/establishments/types";
import { toast } from "react-toastify";

const useEstablishments = () => {
  const { params, setParams, handlePage } = useParams();
  const { per_page, current_page } = params;

  const { establishments, loading, error } = useSelector(
    (state: any) => state.establishments,
  );
  const dispatch: AppDispatch = useDispatch();

  // Estados dos filtros (busca separada para debounce)
  const [filters, setFilters] = useState({
    search: "",
    status: "all" as "all" | "active" | "inactive" | "pending" | "suspended",
    plan_id: "all" as "all" | number,
    city: "",
    state: "",
  });

  // Debounce para busca, cidade e estado
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [debouncedCity, setDebouncedCity] = useState("");
  const [debouncedState, setDebouncedState] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(filters.search.trim()), 400);
    return () => clearTimeout(id);
  }, [filters.search]);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedCity(filters.city.trim()), 400);
    return () => clearTimeout(id);
  }, [filters.city]);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedState(filters.state.trim()), 400);
    return () => clearTimeout(id);
  }, [filters.state]);

  // Busca automática inicial e quando filtros relevantes ou busca (debounced) mudarem
  useEffect(() => {
    const workingFilters = {
      ...filters,
      search: debouncedSearch,
      city: debouncedCity,
      state: debouncedState,
    };

    const payloadFilters = Object.fromEntries(
      Object.entries(workingFilters).filter(([key, value]) => {
        if (key === "search" || key === "city" || key === "state")
          return typeof value === "string" && value !== "";
        if (key === "status" || key === "plan_id") return value !== "all";
        return typeof value === "string" && value !== "";
      }),
    );

    // Mapear corretamente os parâmetros de paginação para a API (page/per_page)
    const payload = {
      page: Number(params.current_page) || 1,
      per_page: Number(params.per_page) || 15,
      ...payloadFilters,
    };
    dispatch(fetchEstablishments(payload));
  }, [
    params,
    debouncedSearch,
    debouncedCity,
    debouncedState,
    filters.status,
    filters.plan_id,
    dispatch,
  ]);

  const handleRefresh = async () => {
    const payload = {
      page: Number(params.current_page) || 1,
      per_page: Number(params.per_page) || 15,
      ...Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          if (key === "search")
            return typeof value === "string" && value.trim() !== "";
          if (key === "status" || key === "plan_id") return value !== "all";
          return typeof value === "string" && value !== "";
        }),
      ),
    };

    try {
      await dispatch(fetchEstablishments(payload)).unwrap();
    } catch (err: any) {
      if (err?.error) {
        toast.error(err.error);
      } else {
        toast.error("Erro ao buscar estabelecimentos.");
      }

      dispatch(clearEstablishments());
    }
  };

  const handleFilterChange = (key: keyof typeof filters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      status: "all",
      plan_id: "all",
      city: "",
      state: "",
    });
  };

  const handleUpdateEstablishment = useCallback(
    async (
      id: number,
      data: {
        name?: string;
        email?: string;
        plan_id?: number;
        is_active?: boolean;
        phone_whatsapp?: string;
        landline?: string;
        cpf_cnpj?: string;
        city?: string;
        state?: string;
        country?: string;
      },
    ) => {
      try {
        const resultAction = await dispatch(updateEstablishment({ id, data }));

        if (updateEstablishment.fulfilled.match(resultAction)) {
          toast.success("Estabelecimento atualizado com sucesso!");
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error("Erro ao atualizar estabelecimento:", error);
        return false;
      }
    },
    [dispatch],
  );

  const handleCreateEstablishment = useCallback(
    async (data: {
      name: string;
      email: string;
      plan_id: number;
      is_active?: boolean;
      phone_whatsapp?: string;
      landline?: string;
      cpf_cnpj?: string;
      city?: string;
      state?: string;
      country?: string;
    }) => {
      try {
        const resultAction = await dispatch(createEstablishment(data));

        if (createEstablishment.fulfilled.match(resultAction)) {
          toast.success("Estabelecimento criado com sucesso!");
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    },
    [dispatch],
  );

  const handleActivateEstablishment = useCallback(
    async (id: number) => {
      try {
        const resultAction = await dispatch(activateEstablishment(id));

        if (activateEstablishment.fulfilled.match(resultAction)) {
          toast.success("Estabelecimento ativado com sucesso!");
          return true;
        } else {
          toast.error("Erro ao ativar estabelecimento.");
          return false;
        }
      } catch (error) {
        toast.error("Erro ao ativar estabelecimento.");
        return false;
      }
    },
    [dispatch],
  );

  const handleDeactivateEstablishment = useCallback(
    async (id: number) => {
      try {
        const resultAction = await dispatch(deactivateEstablishment(id));

        if (deactivateEstablishment.fulfilled.match(resultAction)) {
          toast.success("Estabelecimento desativado com sucesso!");
          return true;
        } else {
          toast.error("Erro ao desativar estabelecimento.");
          return false;
        }
      } catch (error) {
        toast.error("Erro ao desativar estabelecimento.");
        console.error("Erro ao desativar estabelecimento:", error);
        return false;
      }
    },
    [dispatch],
  );

  const handleToggleStatus = useCallback(
    async (establishment: Establishment) => {
      try {
        const newStatus =
          establishment.status === "active" ? "inactive" : "active";

        const resultAction = await dispatch(
          toggleEstablishmentStatus({
            id: establishment.id,
            status: newStatus,
          }),
        );

        if (toggleEstablishmentStatus.fulfilled.match(resultAction)) {
          toast.success("Status alterado com sucesso!");
        } else {
          toast.error("Erro ao alterar status do estabelecimento.");
        }
      } catch (error) {
        toast.error("Erro ao alterar status do estabelecimento.");
        console.error("Erro ao alterar status do estabelecimento:", error);
      }
    },
    [dispatch],
  );

  return {
    establishments: establishments?.data || [],
    pagination: {
      current_page: establishments?.current_page || 1,
      per_page: establishments?.per_page || 15,
      total: establishments?.total || 0,
      last_page: establishments?.last_page || 1,
    },
    filters,
    loading,
    error,
    handleRefresh,
    handleToggleStatus,
    handleActivateEstablishment,
    handleDeactivateEstablishment,
    handleUpdateEstablishment,
    handleCreateEstablishment,
    handleFilterChange,
    handleClearFilters,
    params,
    setParams,
    handlePage,
  };
};

export default useEstablishments;
